import { Ban, CalendarCheck2, CalendarClock, CalendarX2, Mic, RotateCcw, Undo2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { usePermission } from '../../../../auth/AuthContext';
import { useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { todayCalendarDate } from '../../../../components/calendar/monthGrid';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { ErrorState } from '../../../../components/ErrorState';
import { AuditTrailList } from '../../../../components/index-page/AuditTrailList';
import type { MenuItem } from '../../../../components/Menu';
import { PreviewTable } from '../../../../components/PreviewTable';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { Skeleton } from '../../../../components/Skeleton';
import { StatusText } from '../../../../components/table/StatusText';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatClockTime, formatDateOnly, formatDateTime } from '../../../../lib/formatDate';
import { CancelPressConferenceModal } from '../components/CancelPressConferenceModal';
import { PressConferenceModal, type PressConferenceSection } from '../components/PressConferenceModal';
import { pressConferencesApi } from '../pressConferenceApi';
import {
  PRESS_CONFERENCES_PATH,
  PRESS_CONFERENCE_STATUS,
  markHeldBlock,
  reopenBlock,
  pressConferenceLock,
  type PressConferenceItem,
} from '../pressConferenceTypes';
import { personsApi } from '../../../human-resources/personnel/personnelApi';
import { timeRange } from '../utils/timeRange';

type TabKey = 'info' | 'audit';

/**
 * One press conference — its schedule on Info, every write ever made to it on Audit trail.
 *
 * On the **header** layout: a conference reads as one thing, and is opened to check when and
 * where. It LANDS on Info.
 *
 * **The banner states where it stands, for every state**, and owns the lifecycle: Mark held on a
 * scheduled conference whose day has come (it WRITES, so it confirms), Restore on a deleted one.
 * Cancel sits in the header's cog — it collects a reason, so it opens its own form.
 */
export function PressConferenceProfilePage() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();

  const [conference, setConference] = useState<PressConferenceItem | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [tab, setTab] = useState<TabKey>('info');
  const [editing, setEditing] = useState<PressConferenceSection | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [pending, setPending] = useState<'hold' | 'reopen' | 'restore' | 'delete' | null>(null);
  const [running, setRunning] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  // A different conference drops what is on screen first.
  const [shownId, setShownId] = useState(id);
  if (shownId !== id) {
    setShownId(id);
    setConference(null);
    setError(null);
  }

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      pressConferencesApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;
          setConference(next);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this press conference.', 0));
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  /*
    THE NAMES, in ONE batch for every person on the page — the external requestor, the internal one,
    the speakers — and only for a reader holding `personnel.read`, which is what the resolve needs.
    Nothing is stored; a file above the reader's level comes back Restricted. Anyone else is told the
    name is Hidden, which is not an em dash: the record HAS a person and this page was not given it.
  */
  const canReadPeople = usePermission('personnel.read');
  const [names, setNames] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    if (!conference || !canReadPeople) return;

    const ids = [
      conference.requestorPersonId,
      conference.requestorPersonnelPersonId,
      ...(conference.speakers ?? []).map((s) => s.personId),
    ].filter((personId): personId is string => personId != null);

    if (ids.length === 0) return;

    const controller = new AbortController();

    personsApi
      .resolve([...new Set(ids)], controller.signal)
      .then((resolved) => {
        if (!controller.signal.aborted) setNames(new Map(resolved.map((n) => [n.personId, n.displayName])));
      })
      // A failed resolve leaves the names unresolved and the page standing.
      .catch(() => undefined);

    return () => controller.abort();
  }, [conference, canReadPeople]);

  /** A person's name for display — resolved, Hidden without the grant, or nothing where none is recorded. */
  const nameOf = (personId: string | null | undefined): string | null =>
    personId == null ? null : canReadPeople ? (names.get(personId) ?? null) : 'Hidden';

  async function runPending() {
    if (!conference || !pending) return;

    setRunning(true);
    setActionError(null);

    try {
      if (pending === 'hold') await pressConferencesApi.markHeld(conference.id);
      else if (pending === 'reopen') await pressConferencesApi.reopen(conference.id);
      else if (pending === 'delete') await pressConferencesApi.remove(conference.id);
      else await pressConferencesApi.restore(conference.id);

      toast.success(
        `${pending === 'hold' ? 'Marked held' : pending === 'reopen' ? 'Reopened' : pending === 'delete' ? 'Deleted' : 'Restored'} ${conference.referenceNumber}.`,
      );
      setPending(null);
      load();
    } catch (caught) {
      setActionError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'Public Relations',
    { label: 'Press Con', to: PRESS_CONFERENCES_PATH },
    conference?.referenceNumber ?? 'Press conference',
  ];

  // The full-page error is for a failure with NOTHING to show; a failed refresh keeps the page.
  if (error && !conference) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} onRetry={() => load()} />
      </div>
    );
  }

  if (!conference) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-4 p-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-64 w-full max-w-4xl" />
        </div>
      </div>
    );
  }

  const lock = pressConferenceLock(conference);
  const holdBlock = markHeldBlock(conference, todayCalendarDate());
  const when = `${formatDateOnly(conference.scheduledDate)}, ${formatClockTime(conference.startTime)}`;

  /*
    THE LIFECYCLE ENTRIES, ONE array — read by the header's cog AND the Status card's, so the two
    cannot disagree about a key or a reason. Each carries the key of what it does and says why it is
    disabled; a reader without the key does not see it at all.
  */
  const lifecycleItems: MenuItem[] = [
    {
      key: 'hold',
      label: 'Mark held',
      icon: <CalendarCheck2 className="h-4 w-4" aria-hidden="true" />,
      permission: 'press-conferences.mark-held',
      disabledReason: holdBlock,
      onClick: () => setPending('hold'),
    },
    {
      key: 'cancel',
      label: 'Cancel',
      icon: <CalendarX2 className="h-4 w-4" aria-hidden="true" />,
      permission: 'press-conferences.cancel',
      disabledReason: lock,
      onClick: () => setCancelling(true),
    },
    {
      key: 'reopen',
      label: 'Reopen',
      icon: <Undo2 className="h-4 w-4" aria-hidden="true" />,
      permission: 'press-conferences.reopen',
      disabledReason: reopenBlock(conference),
      onClick: () => setPending('reopen'),
    },
  ];

  // The banner's button on a held or cancelled conference — it WRITES, so it confirms.
  const reopenAction = (
    <PermissionGate permission="press-conferences.reopen">
      <Button
        id="press-conference-profile-reopen"
        variant="secondary"
        leading={<Undo2 className="h-4 w-4" aria-hidden="true" />}
        onClick={() => setPending('reopen')}
      >
        Reopen
      </Button>
    </PermissionGate>
  );

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell<TabKey>
        id="press-conference-profile"
        panel={tab === 'audit' ? 'bleed' : 'cards'}
        header={
          <ProfileHeader
            id="press-conference-profile-header"
            icon={<Mic className="h-6 w-6" aria-hidden="true" />}
            title={conference.purpose}
            meta={[conference.referenceNumber, formatDateOnly(conference.scheduledDate), conference.venue]}
            status={
              !conference.isActive ? (
                <StatusText tone="inactive">Deleted</StatusText>
              ) : conference.status === PRESS_CONFERENCE_STATUS.held ? (
                <StatusText tone="active">{conference.statusLabel}</StatusText>
              ) : conference.status === PRESS_CONFERENCE_STATUS.cancelled ? (
                <StatusText tone="inactive">{conference.statusLabel}</StatusText>
              ) : (
                <StatusText tone="pending">{conference.statusLabel}</StatusText>
              )
            }
            createdBy={conference.createdByName}
            createdAt={conference.createdAt}
            settings={{
              onDelete: conference.isActive ? () => setPending('delete') : undefined,
              deletePermission: 'press-conferences.delete',
              menuItems: [
                ...lifecycleItems,
                ...(conference.isActive
                  ? []
                  : [
                      {
                        key: 'restore',
                        label: 'Restore',
                        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
                        permission: 'press-conferences.delete',
                        onClick: () => setPending('restore'),
                      },
                    ]),
              ],
            }}
          />
        }
        banner={
          !conference.isActive ? (
            <ProfileBanner
              id="press-conference-profile-banner"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This press conference is deleted. It is off the register and the calendar, and it can be restored."
              action={
                <PermissionGate permission="press-conferences.delete">
                  <Button
                    id="press-conference-profile-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => setPending('restore')}
                  >
                    Restore
                  </Button>
                </PermissionGate>
              }
            />
          ) : conference.status === PRESS_CONFERENCE_STATUS.held ? (
            <ProfileBanner
              id="press-conference-profile-banner"
              icon={<CalendarCheck2 className="h-4 w-4" aria-hidden="true" />}
              message={`Held on ${formatDateOnly(conference.scheduledDate)} at ${conference.venue}. It can no longer be edited unless it is reopened.`}
              action={reopenAction}
            />
          ) : conference.status === PRESS_CONFERENCE_STATUS.cancelled ? (
            <ProfileBanner
              id="press-conference-profile-banner"
              icon={<CalendarX2 className="h-4 w-4" aria-hidden="true" />}
              message={`Cancelled${conference.cancellationReason != null ? ` — ${conference.cancellationReason}` : ''}. It can no longer be edited unless it is reopened.`}
              action={reopenAction}
            />
          ) : (
            <ProfileBanner
              id="press-conference-profile-banner"
              icon={<CalendarClock className="h-4 w-4" aria-hidden="true" />}
              message={
                holdBlock === null
                  ? `Scheduled for ${when} at ${conference.venue}. Mark it held once it has taken place.`
                  : `Scheduled for ${when} at ${conference.venue}. It can be marked held on or after that day.`
              }
              action={
                // Drawn only once its day has come — before then the sentence says why it is absent.
                holdBlock === null ? (
                  <PermissionGate permission="press-conferences.mark-held">
                    <Button
                      id="press-conference-profile-hold"
                      variant="secondary"
                      leading={<CalendarCheck2 className="h-4 w-4" aria-hidden="true" />}
                      onClick={() => setPending('hold')}
                    >
                      Mark held
                    </Button>
                  </PermissionGate>
                ) : undefined
              }
            />
          )
        }
        tabs={[
          { key: 'info', label: 'Info' },
          { key: 'audit', label: 'Audit trail', permission: 'audit-logs.read' },
        ]}
        activeTab={tab}
        onTabChange={setTab}
      >
        {tab === 'info' ? (
          <>
            <ProfileCard
              id="press-conference-profile-details"
              title="Details"
              onEdit={() => setEditing('details')}
              editPermission="press-conferences.update"
              editDisabledReason={lock}
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Reference', value: conference.referenceNumber, mono: true },
                  { label: 'Purpose', value: conference.purpose },
                  { label: 'Date', value: formatDateOnly(conference.scheduledDate), mono: true },
                  { label: 'Time', value: timeRange(conference), mono: true },
                  { label: 'Venue', value: conference.venue },
                  { label: 'Remarks', value: conference.remarks },
                ]}
              />
            </ProfileCard>

            {/*
              THE STATUS, with its actions in the card's cog (product call, 13 Oct 2026) — the same
              entries as the header's cog and the banner's button, deliberately: an APPROVED DEVIATION
              from "the banner owns the lifecycle; the state gets no card", recorded in CLAUDE.md. No
              Edit — a status is moved by an ACTION, never typed.
            */}
            <ProfileCard id="press-conference-profile-status" title="Status" menuItems={lifecycleItems}>
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Status', value: conference.statusLabel },
                  {
                    // When it was last marked held, cancelled or reopened — one label on every record.
                    label: 'Status changed',
                    value: conference.statusChangedAt != null ? formatDateTime(conference.statusChangedAt) : null,
                    mono: true,
                  },
                  { label: 'Cancellation reason', value: conference.cancellationReason },
                ]}
              />
            </ProfileCard>

            <ProfileCard
              id="press-conference-profile-requestor"
              title="Requestor"
              onEdit={() => setEditing('requestor')}
              editPermission="press-conferences.update"
              editDisabledReason={lock}
            >
              {/* EVERY row renders whatever the side — the table keeps one shape on every record. */}
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Requested by', value: conference.requestorTypeLabel ?? null },
                  { label: 'Organization', value: conference.requestorOrganization ?? null },
                  { label: 'Person', value: nameOf(conference.requestorPersonId) },
                  { label: 'Employee', value: nameOf(conference.requestorPersonnelPersonId) },
                  { label: 'Employee number', value: conference.requestorEmployeeNumber ?? null, mono: true },
                ]}
              />
            </ProfileCard>

            <ProfileCard
              id="press-conference-profile-speakers"
              title="Speakers"
              onEdit={() => setEditing('speakers')}
              editPermission="press-conferences.update"
              editDisabledReason={lock}
            >
              {(conference.speakers ?? []).length === 0 ? (
                <p className="px-4 py-3 text-xs text-slate-500">No speakers yet.</p>
              ) : (
                <table className="w-full table-auto">
                  <thead className="bg-slate-100">
                    <tr className="text-[9px] uppercase tracking-wider text-gray-600 [&>th]:font-normal">
                      <th className="px-3 py-2 text-left">Speaker</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(conference.speakers ?? []).map((speaker) => {
                      const name = nameOf(speaker.personId) ?? speaker.employeeNumber ?? 'Employee';

                      return (
                        <tr key={speaker.personnelId} className="border-t border-slate-200">
                          <td className="px-3 py-2 align-middle">
                            <p className="text-sm text-slate-600">{name}</p>
                            {speaker.employeeNumber != null && speaker.employeeNumber !== name ? (
                              <p className="font-mono text-xs text-slate-500">{speaker.employeeNumber}</p>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </ProfileCard>

            <ProfileCard id="press-conference-profile-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created by', value: conference.createdByName },
                  { label: 'Created', value: formatDateTime(conference.createdAt), mono: true },
                  { label: 'Updated by', value: conference.updatedByName },
                  { label: 'Updated', value: conference.updatedAt != null ? formatDateTime(conference.updatedAt) : null, mono: true },
                ]}
              />
            </ProfileCard>
          </>
        ) : (
          <div className="flex h-full min-h-0 flex-col p-4">
            <AuditTrailList
              module="press-conferences"
              entityId={conference.id}
              emptyMessage="No recorded activity for this press conference yet."
            />
          </div>
        )}
      </ProfileHeaderShell>

      {editing !== null ? (
        <PressConferenceModal
          editing={conference}
          section={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      ) : null}

      {cancelling ? (
        <CancelPressConferenceModal
          conferenceId={conference.id}
          referenceNumber={conference.referenceNumber}
          onClose={() => setCancelling(false)}
          onCancelled={() => {
            toast.success(`Cancelled ${conference.referenceNumber}.`);
            setCancelling(false);
            load();
          }}
        />
      ) : null}

      {pending ? (
        <ConfirmDialog
          open
          onClose={() => {
            setPending(null);
            setActionError(null);
          }}
          onConfirm={() => void runPending()}
          title={
            pending === 'hold'
              ? 'Mark press conference held'
              : pending === 'reopen'
                ? 'Reopen press conference'
                : pending === 'delete'
                ? 'Delete press conference'
                : 'Restore press conference'
          }
          danger={pending === 'delete'}
          requireConfirm
          requireCredential={pending === 'delete'}
          loading={running}
          id={`press-conference-profile-${pending}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {pending === 'hold'
                  ? `${conference.referenceNumber} is recorded as held. After this it cannot be edited or cancelled unless it is reopened.`
                  : pending === 'reopen'
                    ? `${conference.referenceNumber} goes back to Scheduled and can be edited again.${
                        conference.status === PRESS_CONFERENCE_STATUS.cancelled
                          ? ' The cancellation reason leaves the record; the audit trail keeps it.'
                          : ''
                      }`
                    : pending === 'delete'
                    ? `${conference.referenceNumber} leaves the register and the calendar. Nothing is erased, and it can be restored.`
                    : `${conference.referenceNumber} returns to the register and the calendar.`}
              </p>

              {actionError ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {actionError.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </div>
  );
}
